import { FC, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { Button, Grid, Icons, Loading } from '@thirdspacelearning/library';
import { QUERY_STATUS } from '@tsl-frontend/tsl-utilities';

import { SlideCard } from './components';
import SlidesPreviewDialog, { ViewType } from './SlidesPreviewDialog';

import { LearningObjective, Slide } from '@types';
import { ChangePosition, ErrorActionPanel, PageLayout } from '@shared';

import { CurriculumQueryKeys } from '@services';
import { editSlide, getLearningObjective, getSlides } from '@api';

const sx = {
    gridContainer: {
        alignItems: 'center',
    },
};

interface Props {}

const LearningContentPage: FC<Props> = () => {
    const { learningObjectiveId } = useParams();

    const queryClient = useQueryClient();

    const [slidePreviewIndex, setSlidePreviewIndex] = useState<number>(-1);
    const [slidePreviewView, setSlidePreviewView] = useState<ViewType | null>(
        null,
    );

    const { status: loStatus, data: learningObjective } =
        useQuery<LearningObjective>({
            queryKey: [`${CurriculumQueryKeys.LO}-${learningObjectiveId}`],
            queryFn: () => getLearningObjective(Number(learningObjectiveId)),
            enabled: !!learningObjectiveId,
            retry: 1,
            refetchOnWindowFocus: false,
        });

    const { status: slidesStatus, data: slides } = useQuery<Slide[]>({
        queryKey: [`${CurriculumQueryKeys.SLIDES}-${learningObjectiveId}`],
        queryFn: () =>
            getSlides(Number(learningObjectiveId), { sort: 'position' }),
        enabled: !!learningObjective,
        retry: 1,
        refetchOnWindowFocus: false,
    });

    const { mutate: updateSlide, isPending: isEditingSlide } = useMutation({
        mutationFn: (data: { slideId: number; data: any }) =>
            editSlide(data.slideId, Number(learningObjectiveId), data.data),
        onSuccess: (data: Slide) => {
            enqueueSnackbar('Slide edited successfully!', {
                key: 'edit-slide-success',
                testId: 'edit-slide-success',
                variant: 'curriculumSnackbar',
                severity: 'success',
            });

            queryClient.invalidateQueries({
                queryKey: [
                    `${CurriculumQueryKeys.SLIDES}-${learningObjectiveId}`,
                ],
            });
        },
        onError: (error: Error) => {
            enqueueSnackbar(error?.message, {
                title: 'There was an error editing the slide',
                key: 'edit-slide-error',
                testId: 'edit-slide-error',
                variant: 'curriculumSnackbar',
                severity: 'error',
            });
        },
    });

    const showSlidesPreview = (startingIndex = 0) => {
        setSlidePreviewView(ViewType.VIEW);
        setSlidePreviewIndex(startingIndex);
    };

    const editSlidePreview = (startingIndex = 0) => {
        setSlidePreviewView(ViewType.EDIT);
        setSlidePreviewIndex(startingIndex);
    };

    const addSlidePreview = useCallback(() => {
        if (slides) {
            setSlidePreviewView(ViewType.ADD);
            setSlidePreviewIndex(slides.length);
        }
    }, [slides]);

    useEffect(() => {
        if (slides && !slides.length) {
            addSlidePreview();
        }
    }, [slides, addSlidePreview]);

    const changeActive = (slideId: number, active: boolean) => {
        updateSlide({ slideId, data: { active } });
    };

    const changeSlidePosition = (slide: Slide, toPosition: number) => {
        updateSlide({ slideId: slide.id, data: { position: toPosition } });
    };

    const onCloseDialog = () => {
        setSlidePreviewView(null);
    };

    const onClickAdd = () => {
        addSlidePreview();
    };

    const onClickPreview = () => {
        showSlidesPreview();
    };

    const getPageTitle = () => {
        if (learningObjective) {
            return learningObjective.title;
        } else {
            return 'View/Edit Learning Content';
        }
    };

    const pageTitle = getPageTitle();

    const isPending = [loStatus, slidesStatus].some(
        (status) => status === QUERY_STATUS.pending,
    );

    const isError = [loStatus, slidesStatus].some(
        (status) => status === QUERY_STATUS.error,
    );

    const isSuccess = [loStatus, slidesStatus].every(
        (status) => status === QUERY_STATUS.success,
    );

    return (
        <PageLayout
            title={pageTitle}
            buttons={[
                <Button
                    variant="contained"
                    color="secondary"
                    key="addSlide"
                    onClick={onClickAdd}
                >
                    Add a slide
                </Button>,
                <Button
                    color="primary"
                    key="preview"
                    startIcon={<Icons.Visibility />}
                    onClick={onClickPreview}
                >
                    Preview slides
                </Button>,
            ]}
        >
            {isPending && (
                <Loading loadingMessage="Loading learning objectives..." />
            )}

            {isError && (
                <ErrorActionPanel
                    error="There was an error loading the learning objective and slides"
                    message="Please refresh the page and try again. If the problem persists, please let us know."
                />
            )}

            {isSuccess && learningObjective && slides && (
                <>
                    {slides.map((slide, index) => (
                        <Grid container key={slide.id} sx={sx.gridContainer}>
                            <Grid xs={11} item>
                                <SlideCard
                                    slide={slide}
                                    onChangeActive={() =>
                                        changeActive(slide.id, !slide.active)
                                    }
                                    onEdit={() => editSlidePreview(index)}
                                    onPreview={() => showSlidesPreview(index)}
                                />
                            </Grid>
                            <ChangePosition
                                currentPosition={slide.position}
                                onChangePosition={(newPosition) =>
                                    changeSlidePosition(slide, newPosition)
                                }
                                total={slides.length}
                            />
                        </Grid>
                    ))}
                    {slidePreviewView && (
                        <SlidesPreviewDialog
                            open={!!slidePreviewView}
                            onClose={onCloseDialog}
                            startingSlide={slidePreviewIndex}
                            initialViewType={slidePreviewView}
                            learningObjective={learningObjective}
                            editable
                        />
                    )}
                </>
            )}
        </PageLayout>
    );
};

export default LearningContentPage;
